import { ButtonClass, DialogSize, IDialogStepConfig, IMultiStepDialogInstance, launchMultiStepDialog, LazyDialogInstanceType } from "@pjs/core-ui";
import { AsyncModelStatus } from "@pjs/utilities";
import { auditTime, catchError, switchMap, takeUntil, throwError, timer } from "@pjs/observables";
import { tracker } from "@pjs/analytics";
import { assetCoreI18n } from "../../i18n/AssetCoreI18n.const";
import { IAssetSummaryProps } from "../../asset-summary/interfaces/IAssetSummaryProps";
import { IReportModel } from "../interfaces/IReportModel";
import { IReportInappropriateBody } from "../interfaces/IReportInappropriateBody";
import { reportReasonLimit } from "../consts/ReportReasonLimit.const";
import { sendReport } from "./SendReport.function";

export const launchReportDialog = (assetId: string, assetSummary: IAssetSummaryProps, triggerElement: HTMLElement): LazyDialogInstanceType<IReportModel> => {
    const stepOne: IDialogStepConfig<IReportModel, IMultiStepDialogInstance<IReportModel>> = {
        title: assetCoreI18n.getString("report_dialog.titles.report"),
        actions: {
            left: [],
            right: [
                {
                    label: assetCoreI18n.getString("report_dialog.actions.cancel"),
                    ariaLabel: assetCoreI18n.getString("report_dialog.actions.cancel"),
                    buttonClass: ButtonClass.Outlined,
                    dataHook: "modal-cancel-button",
                    disabled: () => false,
                    action: (_model, instance) => {
                        instance.close(() => {
                            if (triggerElement !== null && document.body.contains(triggerElement)) {
                                triggerElement.focus();
                            }
                        });
                    }
                },
                {
                    label: assetCoreI18n.getString("report_dialog.actions.report"),
                    ariaLabel: assetCoreI18n.getString("report_dialog.actions.report"),
                    buttonClass: ButtonClass.Contained,
                    dataHook: "modal-report-button",
                    disabled: (model) => {
                        return model.reason.trim().length === 0 || model.reason.length > reportReasonLimit;
                    },
                    action: (_model, instance) => {
                        /* eslint-disable @typescript-eslint/naming-convention */
                        const params: IReportInappropriateBody = { Reason: instance.model.reason };
                        /* eslint-enable @typescript-eslint/naming-convention */

                        tracker.trackEvent("Reporting content", "click", "Report (Logged in)", instance.model.reason.length);

                        sendReport(params, { assetId: assetId })
                            .pipe(
                                auditTime(700),
                                catchError((err) => timer(700).pipe(switchMap(() => throwError(err)))),
                                takeUntil(instance.destroyObservable)
                            )
                            .subscribe({
                                next: () => instance.updateModel({ status: AsyncModelStatus.Resolved }),
                                error: () => {
                                    instance.updateModel({ status: AsyncModelStatus.Error });
                                    instance.steps.previous();
                                }
                            });
                        instance.steps.next();
                    }
                }
            ]
        },
        component: () => import("../ReportDialog.component").then((module) => module.ReportDialog)
    };

    const stepTwo: IDialogStepConfig<IReportModel, IMultiStepDialogInstance<IReportModel>> = {
        title: assetCoreI18n.getString("report_dialog.titles.submit"),
        actions: {
            left: [],
            right: [
                {
                    label: assetCoreI18n.getString("report_dialog.actions.done"),
                    ariaLabel: assetCoreI18n.getString("report_dialog.actions.done_label"),
                    buttonClass: ButtonClass.Contained,
                    dataHook: "modal-report-button",
                    disabled: (model) => {
                        return model.status !== AsyncModelStatus.Resolved;
                    },
                    action: (_model, instance) => {
                        instance.close(() => {
                            if (triggerElement !== null && document.body.contains(triggerElement)) {
                                triggerElement.focus();
                            }
                        });
                    }
                }
            ]
        },
        component: () => import("../ReportDialogPending.component").then((module) => module.ReportDialogPending)
    };

    return launchMultiStepDialog<IReportModel>({
        size: DialogSize.Small,
        dismissAction: (_model, instance) => {
            instance.close(() => {
                if (triggerElement !== null && document.body.contains(triggerElement)) {
                    triggerElement.focus();
                }
            });
        },
        initialModel: {
            assetSummary: assetSummary,
            reason: "",
            status: null
        },
        steps: [stepOne, stepTwo]
    });
};
